/**
 * abilityCasterVfx — Short-lived aura/burst meshes for self casts (AB-04+).
 * Supports follow-caster auras (Garen E Judgment) and target bursts (Garen R).
 * Ground zones hold full opacity until the last ZONE_OPACITY_FADE_SEC (FID-VFX-01).
 */
import { createAbilityVfxMesh } from './abilityVfx.js';
import { getAbilityVfxSpec } from './abilityVfxSpecs.js';

/** Hold zone/aura opacity until this many seconds remain, then ease out. */
export const ZONE_OPACITY_FADE_SEC = 1;

function casterGround(caster = {}) {
  return {
    x: caster.x ?? 0,
    z: caster.z ?? caster.y ?? 0
  };
}

function placeAt(mesh, x, y, z) {
  if (mesh.position && typeof mesh.position.set === 'function') {
    mesh.position.set(x, y, z);
  }
}

function layoutBeam(mesh, from, to) {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const dist = Math.hypot(dx, dz) || 0.01;
  placeAt(mesh, (from.x + to.x) / 2, 0.6, (from.z + to.z) / 2);
  if (mesh.scale && typeof mesh.scale.set === 'function') {
    mesh.scale.set(1, 1, dist);
  } else if (mesh.scale && typeof mesh.scale.setScalar === 'function') {
    mesh.scale.setScalar(dist);
  }
  if (mesh.rotation) {
    mesh.rotation.y = Math.atan2(dx, dz);
  }
}

export function createAbilityCasterVfx({ THREE, scene } = {}) {
  const active = [];

  function play(vfxId, caster = {}, opts = {}) {
    const spec = getAbilityVfxSpec(vfxId);
    if (!spec || !THREE || !scene) return false;
    const mesh = createAbilityVfxMesh(vfxId, THREE);
    if (!mesh) return false;
    const from = casterGround(caster);
    const target = opts.target;
    if (spec.shape === 'beam' && target) {
      const to = casterGround(target);
      layoutBeam(mesh, from, to);
    } else if (spec.kind === 'ground' && opts.ground) {
      const g = opts.ground;
      placeAt(mesh, g.x ?? 0, 0.05, g.z ?? g.y ?? 0);
    } else if (target && spec.kind === 'burst') {
      const to = casterGround(target);
      placeAt(mesh, to.x, 0.3, to.z);
    } else {
      placeAt(mesh, from.x, 0.2, from.z);
    }
    scene.add(mesh);
    const duration = spec.duration || 0.45;
    active.push({
      mesh,
      life: duration,
      max: duration,
      follow: spec.follow && spec.kind !== 'ground' ? caster : null,
      spin: spec.shape === 'spin' || spec.shape === 'judgment' || mesh.userData?.spin
    });
    return true;
  }

  function tick(dt) {
    for (let i = active.length - 1; i >= 0; i -= 1) {
      const fx = active[i];
      fx.life -= dt;
      if (fx.follow) {
        const g = casterGround(fx.follow);
        placeAt(fx.mesh, g.x, 0.2, g.z);
      }
      if (fx.spin && fx.mesh.rotation) {
        fx.mesh.rotation.y = (fx.mesh.rotation.y || 0) + dt * 14;
      }
      fadeMesh(fx.mesh, opacityForLife(fx.life, fx.max));
      if (fx.life <= 0) {
        scene.remove(fx.mesh);
        active.splice(i, 1);
      }
    }
  }

  return { play, tick, getActiveCount: () => active.length };
}

/** Fade window: last N seconds, or 25% of short flashes (whichever is smaller). */
export function zoneOpacityFadeTail(duration) {
  const d = duration > 0 ? duration : 0.45;
  return Math.min(ZONE_OPACITY_FADE_SEC, d * 0.25);
}

/** Hold full opacity until the last fade tail, then ease out. */
export function opacityForLife(life, max) {
  const duration = max > 0 ? max : 0.45;
  const remaining = Math.max(0, life);
  const fadeTail = zoneOpacityFadeTail(duration);
  if (remaining >= fadeTail) return 1;
  if (fadeTail <= 0) return 0;
  return remaining / fadeTail;
}

function fadeMesh(mesh, t) {
  if (mesh.material) mesh.material.opacity = t;
  const kids = mesh.children;
  if (!kids) return;
  for (let i = 0; i < kids.length; i += 1) {
    if (kids[i].material) kids[i].material.opacity = t;
  }
}

/** Agent smoke: sample hold-until-near-expiry opacity curve (FID-VFX-01). */
export function wireGroundZoneOpacityDebug(globalObj = globalThis) {
  if (typeof globalObj === 'undefined') return null;
  globalObj.__debugGroundZoneOpacity = (life, max) => {
    const duration = max > 0 ? max : 0.45;
    return {
      opacity: opacityForLife(life, duration),
      fadeTail: zoneOpacityFadeTail(duration),
      fadeSec: ZONE_OPACITY_FADE_SEC
    };
  };
  return globalObj.__debugGroundZoneOpacity;
}
